import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

type BedroomMarketRow = {
  bedrooms?: number | string;
  median?: number | null;
  medianDaysOnMarket?: number | null;
  averageDaysOnMarket?: number | null;
  totalListings?: number | null;
};

type MatrixPoint = {
  bedroomLabel: string;
  bedrooms: number | string;
  dom: number;
  yieldPct: number;
  listings: number;
};

const COLORS = ['#d9468f', '#7558d9', '#2778dd', '#13a0a6', '#f28c38', '#3eaf70', '#667eea'];

const OpportunityBubble: React.FC<{
  cx?: number;
  cy?: number;
  size?: number;
  fill?: string;
  payload?: MatrixPoint;
}> = ({ cx = 0, cy = 0, size = 0, fill = '#2778dd', payload }) => {
  const radius = Math.max(16, Math.sqrt(Number(size) / Math.PI));
  if (!payload) return <g />;
  return (
    <g>
      <circle cx={cx} cy={cy} r={radius} fill={fill} stroke="#fff" strokeWidth={2} />
      <text x={cx} y={cy - 2} textAnchor="middle" fill="#fff" fontSize={radius < 20 ? 9 : 11} fontWeight={800}>
        {payload.bedrooms}
      </text>
      <text x={cx} y={cy + 9} textAnchor="middle" fill="#fff" fontSize={radius < 20 ? 7 : 8} fontWeight={700}>
        {Number(payload.bedrooms) === 1 ? 'bed' : 'beds'}
      </text>
    </g>
  );
};

const median = (values: number[]) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const OpportunityMatrixCard: React.FC<{
  saleRows?: BedroomMarketRow[];
  rentalRows?: BedroomMarketRow[];
}> = ({ saleRows, rentalRows }) => {
  const data = useMemo(() => {
    const rentals = new Map((rentalRows || []).map((row) => [String(row.bedrooms), row]));
    return (saleRows || [])
      .map((sale): MatrixPoint | null => {
        const rental = rentals.get(String(sale.bedrooms));
        const salePrice = Number(sale.median) || 0;
        const rent = Number(rental?.median) || 0;
        const dom = Number(sale.medianDaysOnMarket ?? sale.averageDaysOnMarket) || 0;
        if (sale.bedrooms == null || salePrice <= 0 || rent <= 0 || dom <= 0) return null;
        return {
          bedroomLabel: `${sale.bedrooms} ${Number(sale.bedrooms) === 1 ? 'bed' : 'beds'}`,
          bedrooms: sale.bedrooms,
          dom,
          yieldPct: (rent * 12 * 100) / salePrice,
          listings: Number(sale.totalListings) || 1,
        };
      })
      .filter((row): row is MatrixPoint => row != null)
      .sort((a, b) => Number(a.bedrooms) - Number(b.bedrooms));
  }, [rentalRows, saleRows]);

  if (!data.length) return null;

  const domThreshold = median(data.map((row) => row.dom));
  const yieldThreshold = median(data.map((row) => row.yieldPct));
  const maxDom = Math.ceil(Math.max(...data.map((row) => row.dom)) * 1.15);
  const maxYield = Math.ceil(Math.max(...data.map((row) => row.yieldPct)) * 1.15);

  return (
    <div
      style={{
        border: '1px solid #e8eef3',
        borderRadius: 14,
        background: '#fff',
        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#12858d', fontSize: 18, fontWeight: 800, marginBottom: 14 }}>
        <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#dff4f5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>↗</span>
        7. Opportunity Matrix
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#17233d' }}>Bedroom Count Opportunity Matrix</div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
          Market speed versus estimated gross yield; bubble size represents sale listings.
        </div>
        <div style={{ position: 'relative', height: 390, marginTop: 10 }}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 24, right: 24, bottom: 28, left: 10 }}>
              <CartesianGrid stroke="#e7edf2" strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="dom"
                domain={[0, maxDom]}
                name="Sale DOM"
                unit=" days"
                tick={{ fontSize: 10 }}
                label={{ value: 'Sale DOM (days)', position: 'insideBottom', offset: -16, fontSize: 11 }}
              />
              <YAxis
                type="number"
                dataKey="yieldPct"
                domain={[0, maxYield]}
                name="Gross Yield"
                unit="%"
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => `${value}%`}
                width={46}
              />
              <ZAxis type="number" dataKey="listings" range={[800, 2500]} />
              <ReferenceLine x={domThreshold} stroke="#b9c5d2" strokeDasharray="5 4" />
              <ReferenceLine y={yieldThreshold} stroke="#b9c5d2" strokeDasharray="5 4" />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                formatter={(value: number, name: string) => [
                  name === 'Gross Yield' ? `${Number(value).toFixed(1)}%` : Number(value).toLocaleString(),
                  name,
                ]}
              />
              <Scatter data={data} shape={<OpportunityBubble />}>
                {data.map((row, index) => (
                  <Cell key={row.bedroomLabel} fill={COLORS[index % COLORS.length]} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ position: 'absolute', left: 60, top: 8, background: '#e8f7f5', color: '#167c83', borderRadius: 4, padding: '4px 7px', fontSize: 9 }}>High yield / Fast market</div>
          <div style={{ position: 'absolute', right: 16, top: 8, background: '#e8f7f5', color: '#167c83', borderRadius: 4, padding: '4px 7px', fontSize: 9 }}>High yield / Slow market</div>
          <div style={{ position: 'absolute', left: 60, bottom: 32, background: '#edf4ff', color: '#3569a7', borderRadius: 4, padding: '4px 7px', fontSize: 9 }}>Lower yield / Fast market</div>
          <div style={{ position: 'absolute', right: 16, bottom: 32, background: '#edf4ff', color: '#3569a7', borderRadius: 4, padding: '4px 7px', fontSize: 9 }}>Lower yield / Slow market</div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityMatrixCard;
